import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Printer, Lock, CheckCircle, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  updatePrintingSettings,
  testPrintNodeKey,
  listPrintNodePrinters,
} from '../lib/api';

/**
 * Admin-only card for configuring physical-label printing.
 *
 * Three modes:
 *   - browser   : window.print() on a hidden iframe (zero setup, any printer)
 *   - zebra     : POST raw ZPL to a local Zebra Browser Print agent
 *   - printnode : backend proxies to PrintNode cloud API (per-tenant API key)
 */
export default function PrintSettingsCard({ company, onSaved }) {
  const initialMode = company?.print_mode || 'browser';
  const [mode, setMode] = useState(initialMode);
  const [zebraUrl, setZebraUrl] = useState(company?.zebra_browser_print_url || 'http://localhost:9100');
  const [autoPrintOnShip, setAutoPrintOnShip] = useState(!!company?.auto_print_on_ship);
  const [pnKey, setPnKey] = useState('');
  const [pnPrinterId, setPnPrinterId] = useState(company?.printnode_printer_id || '');
  const [pnPrinters, setPnPrinters] = useState([]);
  const [savingMode, setSavingMode] = useState(false);
  const [savingKey, setSavingKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [loadingPrinters, setLoadingPrinters] = useState(false);

  const keyMasked = company?.printnode_api_key_masked;

  useEffect(() => {
    setMode(company?.print_mode || 'browser');
    setZebraUrl(company?.zebra_browser_print_url || 'http://localhost:9100');
    setPnPrinterId(company?.printnode_printer_id || '');
    setAutoPrintOnShip(!!company?.auto_print_on_ship);
  }, [company?.print_mode, company?.zebra_browser_print_url, company?.printnode_printer_id, company?.auto_print_on_ship]);

  // Auto-load PrintNode printers when mode is printnode and a key is saved
  useEffect(() => {
    if (mode !== 'printnode' || !keyMasked) return;
    refreshPrinters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, keyMasked]);

  const refreshPrinters = async () => {
    setLoadingPrinters(true);
    try {
      const res = await listPrintNodePrinters();
      setPnPrinters(res?.data?.printers || []);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to load printers';
      toast.error(msg);
    } finally {
      setLoadingPrinters(false);
    }
  };

  const saveModeAndZebra = async (patch) => {
    setSavingMode(true);
    try {
      await updatePrintingSettings(patch);
      toast.success('Print settings saved');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingMode(false);
    }
  };

  const handleModeChange = (next) => {
    setMode(next);
    saveModeAndZebra({ print_mode: next });
  };

  const handleZebraUrlSave = () => {
    saveModeAndZebra({ zebra_browser_print_url: zebraUrl });
  };

  const handlePrinterSave = (id) => {
    setPnPrinterId(id);
    saveModeAndZebra({ printnode_printer_id: parseInt(id, 10) });
  };

  const handleTestKey = async () => {
    setTesting(true);
    try {
      const res = await testPrintNodeKey(pnKey || undefined);
      if (res?.data?.ok) {
        const acc = res.data.account || {};
        toast.success(`PrintNode OK · ${acc.email || acc.id || 'account verified'}`);
      } else {
        toast.error(res?.data?.message || 'PrintNode key validation failed');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'PrintNode test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSaveKey = async () => {
    if (!pnKey.trim()) {
      toast.error('Paste a PrintNode API key first');
      return;
    }
    setSavingKey(true);
    try {
      await updatePrintingSettings({ printnode_api_key: pnKey.trim() });
      setPnKey('');
      toast.success('PrintNode key saved');
      onSaved?.();
      // Auto-load printers right after saving
      setTimeout(refreshPrinters, 200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save key');
    } finally {
      setSavingKey(false);
    }
  };

  const handleClearKey = async () => {
    if (!window.confirm('Remove the saved PrintNode API key? Print jobs will fall back to browser mode.')) return;
    setSavingKey(true);
    try {
      await updatePrintingSettings({ printnode_api_key: '' });
      setPnKey('');
      setPnPrinters([]);
      toast.message('PrintNode key cleared');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to clear key');
    } finally {
      setSavingKey(false);
    }
  };

  return (
    <div data-testid="print-settings-card" className="border border-border bg-card p-4 mb-6 space-y-4">
      <div className="flex items-center gap-2">
        <Lock size={14} weight="fill" className="text-brand" />
        <h2 className="text-xs font-bold uppercase tracking-widest">Label printing · admin only</h2>
      </div>

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <label className="overline">Active print mode</label>
          <Select value={mode} onValueChange={handleModeChange} disabled={savingMode}>
            <SelectTrigger data-testid="print-mode-select" className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="browser">Browser print dialog · works with any printer</SelectItem>
              <SelectItem value="zebra">Zebra Browser Print · silent ZPL to local agent</SelectItem>
              <SelectItem value="printnode">PrintNode cloud · any printer, paid SaaS</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground mt-1">
            {mode === 'browser' && 'No setup. Click Print → browser dialog opens with the label PDF.'}
            {mode === 'zebra' && 'Install the free Zebra Browser Print agent on each warehouse PC. Prints silently — no dialog.'}
            {mode === 'printnode' && 'Paste your PrintNode API key below + pick a printer. Backend will route jobs to PrintNode\'s cloud.'}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-4 pt-3 border-t border-border">
        <div>
          <div className="text-sm font-bold">Auto-print on ship</div>
          <div className="text-[11px] text-muted-foreground">
            Automatically fire the print dispatcher right after Ship Now / Bulk Ship Now — zero clicks from picked → label printed.
          </div>
        </div>
        <Switch
          checked={autoPrintOnShip}
          disabled={savingMode}
          onCheckedChange={(v) => {
            setAutoPrintOnShip(v);
            saveModeAndZebra({ auto_print_on_ship: v });
          }}
          data-testid="auto-print-on-ship-switch"
        />
      </div>

      {mode === 'zebra' && (
        <div className="border-t border-border pt-3">
          <label className="overline">Zebra Browser Print agent URL</label>
          <div className="flex gap-2 mt-1">
            <Input
              value={zebraUrl}
              onChange={(e) => setZebraUrl(e.target.value)}
              placeholder="http://localhost:9100"
              data-testid="zebra-url-input"
              className="font-mono text-sm"
            />
            <Button onClick={handleZebraUrlSave} disabled={savingMode} variant="outline" data-testid="zebra-url-save-btn">Save</Button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">
            Default localhost:9100 covers a single-PC setup. For shared/cluster setups, point at the agent's hostname/port.
          </p>
        </div>
      )}

      {mode === 'printnode' && (
        <div className="border-t border-border pt-3 space-y-3">
          <div>
            <label className="overline">PrintNode API key</label>
            {keyMasked ? (
              <div className="flex items-center gap-2 mt-1" data-testid="printnode-key-saved">
                <CheckCircle size={16} weight="fill" className="text-green-600" />
                <span className="font-mono text-sm">{keyMasked}</span>
                <Button onClick={handleTestKey} disabled={testing} size="sm" variant="outline" data-testid="printnode-test-btn">
                  {testing ? 'Testing…' : 'Test'}
                </Button>
                <Button onClick={handleClearKey} disabled={savingKey} size="sm" variant="ghost" className="text-red-600" data-testid="printnode-clear-btn">
                  Remove
                </Button>
              </div>
            ) : (
              <div className="flex gap-2 mt-1">
                <Input
                  type="password"
                  value={pnKey}
                  onChange={(e) => setPnKey(e.target.value)}
                  placeholder="Paste your PrintNode API key"
                  data-testid="printnode-key-input"
                  className="font-mono text-sm"
                />
                <Button onClick={handleTestKey} disabled={testing || !pnKey.trim()} variant="outline" data-testid="printnode-test-btn">
                  {testing ? 'Testing…' : 'Test'}
                </Button>
                <Button onClick={handleSaveKey} disabled={savingKey || !pnKey.trim()} className="bg-brand hover:bg-brand/90" data-testid="printnode-save-btn">
                  {savingKey ? 'Saving…' : 'Save'}
                </Button>
              </div>
            )}
            <p className="text-[11px] text-muted-foreground mt-1">
              Get a key at app.printnode.com → Account → API Keys. We never display the saved key in plaintext.
            </p>
          </div>

          {keyMasked && (
            <div>
              <div className="flex items-center justify-between">
                <label className="overline">Default printer</label>
                <Button onClick={refreshPrinters} disabled={loadingPrinters} size="sm" variant="ghost" className="h-6 text-xs" data-testid="printnode-refresh-btn">
                  {loadingPrinters ? 'Loading…' : 'Refresh'}
                </Button>
              </div>
              <Select
                value={pnPrinterId ? String(pnPrinterId) : ''}
                onValueChange={handlePrinterSave}
                disabled={savingMode || loadingPrinters || pnPrinters.length === 0}
              >
                <SelectTrigger data-testid="printnode-printer-select" className="mt-1">
                  <SelectValue placeholder={pnPrinters.length === 0 ? 'No printers — install PrintNode Client' : 'Select a printer'} />
                </SelectTrigger>
                <SelectContent>
                  {pnPrinters.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.name}
                      {p.state !== 'online' && <span className="text-amber-600 ml-2">· {p.state}</span>}
                      {p.computer_name && <span className="text-muted-foreground ml-2">· {p.computer_name}</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {pnPrinters.length === 0 && !loadingPrinters && (
                <p className="text-[11px] text-amber-600 mt-1 flex items-center gap-1">
                  <Warning size={12} weight="fill" /> No printers found. Install the PrintNode Client on a PC connected to your printer.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
